import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import bannerImage from '~/assets/images/banner-team-discussion.jpg';
import AgeField from '~/components/forms/Tools/CalculateMaximumMortgage/AgeField';
import IncomeFieldWithCalculator from '~/components/forms/Tools/CalculateMaximumMortgage/IncomeFieldWithCalculator';
import { Button } from '~/components/ui/Button';
import Container from '~/components/ui/Container';
import Panel from '~/components/ui/Panel';
import {
  textContent,
  type Tile,
} from '~/pages/Tools/CalculateMaximumMortgage/Index/data';
import type { CalculateMaximumMortgageFormValues } from '~/pages/Tools/CalculateMaximumMortgage/Index/schema';

const IndexBannerSection: React.FC = () => {
  const form = useFormContext<CalculateMaximumMortgageFormValues>();

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      const response = await fetch('/hypotheek/maximale-hypotheek-berekenen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        console.error('Server returned an error', await response.text());
        return;
      }

      const result = await response.json();

      if (result.redirectUrl) {
        window.location.href = result.redirectUrl;
      } else {
        console.error('No redirect URL returned from server.');
      }
    } catch (error) {
      console.error('Error posting data:', error);
    }
  });

  const { tiles } = textContent;

  return (
    <>
      <section className="relative bg-blue-700 pt-14 md:pt-28">
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-top opacity-35"
            style={{ backgroundImage: `url(${bannerImage})` }}
          />
          <div
            className="absolute bottom-0 left-1/2 z-10 h-[10vh] w-[130%] -translate-x-1/2 translate-y-1/2 -rotate-2 bg-white"
            style={{ boxShadow: '0 0 60px rgba(17, 15, 18, 0.7)' }}
          />
        </div>
        <Container className="relative z-20">
          <div className="grid gap-6 md:grid-cols-10">
            <div className="text-shadow-lg font-display text-white md:col-span-6">
              <h1 className="mb-5 text-4xl font-bold md:text-6xl">
                Maximale
                <br />
                hypotheek
                <br />
                berekenen
              </h1>
              <h2 className="mb-5 text-xl md:text-2xl text-balance">
                Bereken eenvoudig in een paar stappen jouw maximale hypotheek
              </h2>
            </div>
            <div className="md:col-span-4">
              <h3 className="mb-5 font-display text-xl font-bold text-white">
                Bereken je maximale hypotheek
              </h3>
              <Panel className="!mb-0">
                <form onSubmit={onSubmit}>
                  <div>
                    <IncomeFieldWithCalculator
                      name="income"
                      label="Mijn bruto jaarinkomen"
                      placeholder="Bruto jaarinkomen"
                    />
                    <div>
                      <AgeField
                        name="age"
                        label="Leeftijd"
                        placeholder="Leeftijd"
                      />
                    </div>
                  </div>
                  <div className="mt-6">
                    <Button type="submit" className="w-full">
                      Hypotheek berekenen
                      <FontAwesomeIcon icon={faArrowRight} />
                    </Button>

                    <a
                      href="/hypotheek/maximale-hypotheek-berekenen/berekenen"
                      className="mt-4 inline-block"
                    >
                      Uitgebreid mijn hypotheek berekenen
                    </a>
                  </div>
                </form>
              </Panel>
            </div>
          </div>
        </Container>
      </section>

      {tiles && (
        <section className="py-14">
          <Container>
            <h2 className="mb-8 text-center font-display text-xl font-bold text-blue-600 md:text-left md:text-3xl">
              Hoe werkt het?
            </h2>

            <div className="grid gap-3 md:grid-cols-3 md:gap-6">
              {tiles.map(({ icon, title, content }: Tile, index) => (
                <Panel className="relative overflow-hidden text-center">
                  <div className="relative z-20 flex flex-col items-center gap-6">
                    <div className="h-[105px] flex items-center justify-center">
                      <img
                        className="w-24"
                        src={icon}
                        alt={title}
                      />
                    </div>
                    <h3 className="font-display text-2xl font-bold text-black">
                      {title}
                    </h3>
                    <p className="text-lg">{content}</p>
                  </div>
                  <span
                    className="pointer-events-none absolute bottom-0 right-0 translate-y-1/3 font-display text-[264px] font-black italic text-stone-50/50"
                    role="presentation"
                  >
                    {index + 1}
                  </span>
                </Panel>
              ))}
            </div>
          </Container>
        </section>
      )}
    </>
  );
};

export default IndexBannerSection;
